// React
import React, {
    useEffect,
    useState,
} from 'react';
import {
    Button,
    TextField,
    Box,
    Rating,
    useTheme,
    SvgIcon,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarRateIcon from '@mui/icons-material/StarRate';
import Modal from 'components/Modal';
import PropTypes from 'prop-types';
import { ROUTES } from 'services/utils/enums';
import { sendRequest } from 'services/api';
import axios from 'axios';

export const ReviewModal = ({
    jobID,
    repairerCompany,
    open,
    onClose,
    onSubmit,
}) => {
    const theme = useTheme();
    const [rating, setRating] = useState(0);
    const [note, setNote] = useState('');
    const [focused, setFocused] = useState(false);
    const [success, setSuccess] = useState(false);

    const OutlinedStar = ({ fill = 'transparent', sx = {}, stroke = 0.5, strokeColor = '#f5c518' }) => (
        <SvgIcon sx={{ fontSize: 'inherit', ...sx }} viewBox='0 0 24 24'>
            <path
                d='M12 17.27L18.18 21 16.54 13.97 22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z'
                fill={fill}
                stroke={strokeColor}
                strokeWidth={stroke}
            />
        </SvgIcon>
    );

    const submitReview = async (repairerCompany) => {
        const response = await sendRequest(
            ROUTES.CREATE_COMPANY_REVIEW,
            repairerCompany.id,
            {
                // eslint-disable-next-line camelcase
                job_id: jobID,
                rating,
                comment: note,
            },
        );
        return response;
    };

    const renderLabel = () =>
        focused || note !== ''
            ? <span style={{ color: theme.palette.primary[700] }}>Leave a note</span>
            : (
                <span style={{ color: theme.palette.grey[700] }}>
                    Leave a note <span style={{ color: theme.palette.grey[400] }}>optional</span>
                </span>
            );

    OutlinedStar.propTypes = {
        fill: PropTypes.string,
        sx: PropTypes.object,
        stroke: PropTypes.number,
        strokeColor: PropTypes.string,
    };

    const showReviewForm = (repairerCompany) => {
        return (
            <Box
                tabIndex={-1}
                display='flex'
                flexDirection='column'
                alignItems='center' mt={2}
                gap={2}
                sx={{
                    outline: 'none',
                    '&:focus': {
                        outline: 'none',
                    },
                    '&:focus-visible': {
                        outline: 'none',
                    },
                    boxShadow: 'none',
                }}
            >
                <Rating
                    name='vendor-rating'
                    value={rating}
                    onChange={(_, value) => setRating(value)}
                    icon={<OutlinedStar />}
                    emptyIcon={<OutlinedStar />}
                    size='large'
                    sx={{
                        fontSize: '65px',
                        transition: 'none',
                        '& .MuiRating-icon': {
                            transition: 'none',
                            transform: 'none',
                        },
                        '& .MuiRating-iconEmpty path': {
                            fill: 'transparent',
                            stroke: theme.palette.yellow[500],
                        },
                        '& .MuiRating-iconFilled path': {
                            fill: theme.palette.yellow[500],
                            stroke: theme.palette.yellow[500],
                        },
                        '& .MuiRating-iconHover path': {
                            fill: theme.palette.yellow[100],
                            stroke: theme.palette.yellow[500],
                        },
                    }}
                />

                <TextField
                    label={renderLabel()}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    onFocus={(e) => setFocused(true)}
                    onBlur={(e) => setFocused(false)}
                    fullWidth
                    multiline
                    minRows={1}
                    variant='outlined'
                    size='small'
                    sx={{
                        maxWidth: '384px',
                        '& .MuiFormLabel-root': {
                            color: focused ? theme.palette.primary.main : theme.palette.grey[700],
                        },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                            transition: 'border-color 0.2s ease-in-out',
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: theme.palette.primary.main,
                                borderWidth: '2px',
                            },
                            '& legend': {
                                width: 'auto',
                                paddingRight: (focused ? '12px' : '0'),
                            },
                        },
                    }}
                />

                <Button
                    variant='contained'
                    onClick={async () => {
                        try {
                            const response = await submitReview(repairerCompany);
                            if (response?.data?.status === 200) {
                                setSuccess(true);
                            }
                        } catch (error) {
                            console.error('Error submitting review:', error);
                        }
                    }}
                    disabled={!rating}
                    sx={{
                        width: '384px',
                        backgroundColor: rating ? theme.palette.blue.main : theme.palette.grey[50],
                        color: rating ? theme.palette.common.white : theme.palette.grey[200],
                        textTransform: 'uppercase',
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: rating ? theme.palette.blue.dark : theme.palette.grey[100],
                        },
                    }}
                >
                Submit
                </Button>
            </Box>
        );
    };

    const showSuccessMessage = (repairerCompany) => {
        return (
            <Box
                tabIndex={-1}
                display='flex'
                flexDirection='column'
                alignItems='center' mt={2}
                gap={2}
                sx={{
                    outline: 'none',
                    '&:focus': {
                        outline: 'none',
                    },
                    '&:focus-visible': {
                        outline: 'none',
                    },
                    boxShadow: 'none',
                }}
            >
                <CheckCircleIcon sx={{ color: theme.palette.primary[500], fontSize: 64 }} />

                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    {repairerCompany.name}
                    &nbsp;&nbsp;&nbsp;
                    {rating}
                    <StarRateIcon
                        sx={{
                            color: theme.palette.yellow[500],
                            fontSize: '20px',
                        }}
                    />
                </Box>

                <Button
                    variant='contained'
                    onClick={async () => {
                        const result = {
                            rating,
                            note,
                            jobID,
                            repairerCompany,
                        };
                        onSubmit(result);
                        onClose();

                        setTimeout(() => {
                            setSuccess(false);
                            setFocused(false);
                            setNote('');
                            setRating(0);
                        }, 2000);
                    }}
                    disabled={rating === 0 || rating === null}
                    sx={{
                        width: '384px',
                        backgroundColor: rating ? theme.palette.blue.main : theme.palette.grey[50],
                        color: rating ? theme.palette.common.white : theme.palette.grey[200],
                        textTransform: 'uppercase',
                        borderRadius: 2,
                        boxShadow: 'none',
                        '&:hover': {
                            backgroundColor: rating ? theme.palette.blue.dark : theme.palette.grey[100],
                        },
                    }}
                >
                Close
                </Button>
            </Box>
        );
    };

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={success ? 'Success!' : 'Rate this vendor'}
            subTitle={success
                ? 'We value your feedback'
                : 'Based on your experience with this vendor, please rate them with 1 star being terrible and 5 stars being excellent'
            }
            width='812px'
        >
            {success ? showSuccessMessage(repairerCompany) : showReviewForm(repairerCompany)}
        </Modal>
    );
};

ReviewModal.propTypes = {
    open: PropTypes.bool,
    jobID: PropTypes.string,
    repairerCompany: PropTypes.object,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
};